<template>
<div>
    <md-dialog-confirm
        :md-title="confirmPromote.title"
        :md-content-html="promoteContent"
        :md-ok-text="confirmPromote.ok"
        :md-cancel-text="confirmPromote.cancel"
        @close="promoteVersionConfirm"
        ref="promoteVersionDialog">
    </md-dialog-confirm>
    <md-dialog-confirm
        :md-title="confirmDelete.title"
        :md-content-html="confirmDelete.content"
        :md-ok-text="confirmDelete.ok"
        :md-cancel-text="confirmDelete.cancel"
        @close="deleteVersionConfirm"
        ref="deleteVersionDialog">
    </md-dialog-confirm>
    <md-sidenav class="md-left" ref="versionsNav">
        <md-toolbar class="md-dense main-toolbar">
            <md-button class="md-icon-button" @click="back()">
                <md-icon>menu</md-icon>
            </md-button>
            <h3 class="md-title">History</h3>
        </md-toolbar>
        <form novalidate @submit.stop.prevent="saveVersion" v-if="activeProjectEntitled">
            <md-input-container>
                <label for="version-name">Version Name</label>
                <md-input id="version-name" required v-model="versionTitle" @input="checkVersionTitle"></md-input>
            </md-input-container>
            <div class="comm-error">
                <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
            </div>
            <md-button class="md-raised md-primary" @click="saveVersion">Save New Version</md-button>
        </form>

        <md-list class="ql-version-list md-double-line">
            <md-list-item>
                <div class="md-list-text-container">
                    <span>Current Version</span>
                    <span v-if="!currentVersion">Live Edit</span>
                    <span v-else>{{currentVersion.name}}</span>
                </div>
            </md-list-item>
            <md-list-item>
                <md-icon v-if="!currentVersion">label</md-icon>
                <md-icon v-else>label_outline</md-icon>
                <div class="md-list-text-container">
                    <span>Live Version</span>
                </div>
                <md-button class="md-icon-button md-list-action" @click="setActiveVersion(null)">
                    <md-icon>launch</md-icon>
                    <md-tooltip md-direction="top">Show Version</md-tooltip>
                </md-button>
            </md-list-item>
            <md-list>
                <!-- The versions the user named, newest day first, at the top (Josh, 6 Oct). -->
                <md-list-item v-for="(filteredVersions, day) in days" :key="day" md-expand-multiple :ref="'day-' + day">
                    <span><strong>{{day}}</strong></span>
                    <md-list-expand>
                        <md-list>
                            <version-element v-for="version in filteredVersions" :key="version.id" 
                                :project="project" 
                                :version="version"
                                :fresh="version.id === freshVersionId"
                                @promoteVersion="promoteVersion"
                                @deleteVersion="deleteVersion"
                                @close="emitClose"
                            >
                            </version-element>
                        </md-list>
                    </md-list-expand>
                </md-list-item>
                <!-- Automatic saves, one folder per kind, below the dated folders (history-autosave.js). -->
                <md-list-item v-for="folder in autoFolders" :key="folder.kind" md-expand-multiple class="ql-auto-folder"
                    @click="toggleFolder(folder.kind)">
                    <md-icon>{{openFolders[folder.kind] ? 'folder_open' : 'folder'}}</md-icon>
                    <div class="md-list-text-container">
                        <span><strong>{{folder.title}}</strong> <span v-if="autoSaveCapEnabled" class="ql-folder-count">· {{folder.versions.length}} of {{autoSaveCap}}</span><span v-else class="ql-folder-count">({{folder.versions.length}})</span></span>
                    </div>
                    <md-list-expand>
                        <md-list>
                            <version-element v-for="version in folder.versions" :key="version.id"
                                :project="project"
                                :version="version"
                                :auto-kind="folder.kind"
                                @promoteVersion="promoteVersion"
                                @deleteVersion="deleteVersion"
                                @close="emitClose"
                            >
                            </version-element>
                        </md-list>
                    </md-list-expand>
                </md-list-item>
                <!-- Only where the cap applies. Both claims hold in code: selectPrune only ever picks
                     versions classifyVersion puts in a folder, and renameVersion writes autoSave: null. -->
                <li v-if="autoSaveCapEnabled && autoFolders.length" class="ql-history-help">
                    Each folder keeps its {{autoSaveCap}} most recent. Manual saves are kept until you delete them. Rename one to keep it.
                </li>
            </md-list>
        </md-list>
    </md-sidenav>
</div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';

import NavBus from '@/buses/nav';
import { db } from '@/main';
import HistoryRevisionsNav from '@/components/history/revisions-nav';
import VersionElement from '@/components/history/version';
import { classifyVersion, AUTO_SAVE_CAP } from '@/lib/history-autosave';

// The folders of automatic saves, in the order shown. The "N of 10" counts and the help
// line appear only on projects whose automatic saves are capped (`autoSaveCap`, stamped
// at creation since Oct 2026).
const AUTO_FOLDERS = [
    {
        kind: 'page_op',
        title: 'Page changes'
    },
    {
        kind: 'promote_backup',
        title: 'Previous live versions'
    }
];

const VersionsNav = {
    name: 'VersionsNav',
    data: function() {
        return {
            revisionVersion: {},
            versionTitle: '',
            versionToPromote: null,
            versionToDelete: null,
            errorMessage: '',
            // The version just saved from this panel, tinted for ~2 s.
            freshVersionId: null,
            autoSaveCap: AUTO_SAVE_CAP,
            // Which automatic-saves folders are open, for the folder icon.
            openFolders: { page_op: false, promote_backup: false },
            // Appended to the promote dialog only when the version predates
            // structure snapshotting (#386). Empty for every version saved after
            // that fix, so this warning extinguishes itself over time.
            promoteStructureNote: '',
            confirmPromote: {
                title: 'Confirm Live Version Replacement',
                ok: 'yes',
                cancel: 'cancel',
                // Rewritten 23 Aug 2026. The old copy ended "You won't be able to
                // undo this change", which became FALSE the moment promotion started
                // saving a backup first. A user could have declined a promote they
                // wanted, believing it was irreversible.
                //
                // Tells rather than asks, deliberately. On a page op "Proceed Without
                // Saving" is reasonable: those are frequent and small, and always
                // saving would flood history with PDF copies. Promotion is rare and
                // replaces the entire project, so skipping the backup has no upside
                // and one very bad outcome. Wording borrows the page-op modal's
                // "Cuelist saves a backup" voice so it reads as the same system.
                content: 'You are about to make a previous version live. This will replace your current live project, including its cues, notes, layers and structure.<br><br>Before that happens, Cuelist saves your current project to history as a backup, so you can go back to it if you change your mind.'
            },
            confirmDelete: {
                title: 'Confirm Delete Version',
                ok: 'yes',
                cancel: 'cancel',
                content: 'You are about to delete an old saved version of this project. You won\'t be able to undo this change.'
            }
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'user',
            'versions',
            'currentVersion',
            'activeProjectEntitled'
        ]),
        autoSaveCapEnabled() {
            return !!(this.project && this.project.autoSaveCap === true);
        },
        promoteContent() {
            const keepNote = this.autoSaveCapEnabled ? ' Cuelist keeps your last 10 previous live versions.' : '';
            return this.confirmPromote.content + keepNote + this.promoteStructureNote;
        },
        // Each folder, newest first across all days. Empty folders are not shown.
        autoFolders() {
            return AUTO_FOLDERS.map(folder => Object.assign({}, folder, {
                versions: this.versions
                    .filter(v => classifyVersion(v) === folder.kind)
                    .sort((a, b) => moment(b.created).diff(moment(a.created)))
            })).filter(folder => folder.versions.length > 0);
        },
        // The dated folders hold only the versions the user named.
        days() {
            const days = this.versions.filter(v => classifyVersion(v) === null).reduce((rv, x) => {
                const day = moment(x.created).format('MM/DD/YYYY');
                if (!rv[day]) {
                    rv[day] = [];
                }
                rv[day].push(x);
                return rv;
            }, {});
            const daysOrdered = {};
            for (const key of Object.keys(days).sort(this.descendingDateComparison)) {
                daysOrdered[key] = days[key].sort((a, b) => {
                    return moment(b.created).diff(moment(a.created));
                });
            }

            return daysOrdered;
        }
    },
    methods: {
        emitClose() {
            this.closeVersionsNav();
        },
        checkVersionTitle() {
            this.errorMessage = '';
        },
        closeVersionsNav() {
            this.$refs.versionsNav.close();
        },
        back() {
            this.closeVersionsNav();
            NavBus.$emit('main-nav.open');
        },
        setActiveVersion(versionID) {
            if (versionID === null) {
                window.location.href = `/project/${this.project.id}`;
            }
        },
        // Dims the app while saving (Josh, 5 Oct; kept 13 Oct with a toast when done):
        // the copy (the server's on switched-on shows, otherwise the browser's after the
        // PDF copy returns) reads the show during the wait, so edits made meanwhile
        // could otherwise land in the version.
        async saveVersion() {
            this.errorMessage = '';
            if (this.versionTitle !== '') {
                const name = this.versionTitle;
                this.$store.dispatch('setLoader', { type: true });
                try {
                    const versionID = await this.$store.dispatch('createVersion', name);
                    this.versionTitle = '';
                    this.errorMessage = '';
                    this.$store.dispatch('setLoader', { type: false });
                    if (this.$toast) {
                        this.$toast.success(`Saved "${name}" to History.`);
                    }
                    this.showSavedVersion(versionID);
                } catch (error) {
                    console.error('Error saving version:', error);
                    this.$store.dispatch('setLoader', { type: false });
                    this.errorMessage = 'Failed to save version. Please try again.';
                }
            } else {
                this.errorMessage = 'Please enter a version name.';
            }
        },
        toggleFolder(kind) {
            this.openFolders[kind] = !this.openFolders[kind];
        },
        // Opens the new version's day folder and tints its row for ~2 s. The binding
        // can lag the save by a moment, so wait (up to ~3 s) for the row to exist.
        showSavedVersion(versionID, attempt) {
            const tries = attempt || 0;
            const version = this.versions.find(v => v.id === versionID);
            const refs = version ? this.$refs['day-' + moment(version.created).format('MM/DD/YYYY')] : null;
            const folder = refs && (Array.isArray(refs) ? refs[0] : refs);
            if (!folder) {
                if (tries < 30) {
                    setTimeout(() => this.showSavedVersion(versionID, tries + 1), 100);
                }
                return;
            }
            if (!folder.active && folder.toggleExpandList) {
                folder.toggleExpandList();
            }
            this.freshVersionId = versionID;
            setTimeout(() => {
                if (this.freshVersionId === versionID) this.freshVersionId = null;
            }, 2000);
        },
        async promoteVersion(versionID) {
            this.versionToPromote = versionID;
            this.promoteStructureNote = await this.buildPromoteStructureNote(versionID);
            this.$refs.promoteVersionDialog.open();
        },
        // ONE case gets a note: a version saved before #386, where structure was
        // never recorded. Promoting one KEEPS the live structure rather than
        // replacing it — deleting marks the version holds no copy of would destroy
        // work during an action that does not present itself as destructive to
        // structure — but the kept marks describe the CURRENT document, so
        // afterwards they can sit on the wrong pages while looking perfectly normal.
        //
        // ⚠ WITHOUT THE NOTE THE DIALOG IS WRONG for those versions, not merely
        // terse: its first sentence promises structure WILL be replaced, and for a
        // pre-#386 version that is false. This is the only thing that says so.
        //
        // Every other case returns '' and the dialog stands on its own: a version
        // carrying its own markers, a project with no structure at all, and — since
        // 13 Sept — a version the fixed code wrote, where structure now behaves
        // exactly like cues, notes and layers.
        async buildPromoteStructureNote(versionID) {
            // Read the LIVE set explicitly. `scriptMarkers` is version-aware, so
            // while viewing the very version being promoted it would return that
            // version's markers — the opposite of what this warning is about.
            const live = Object.values(this.project.scriptMarkers || {});
            if (!live.length) {
                return '';
            }
            try {
                const snapshot = await db.collection('projects').doc(this.project.id)
                    .collection('versions').doc(versionID)
                    .collection('scriptMarkers')
                    .get();
                if (snapshot.docs.length) {
                    return '';
                }
                const version = this.versions.find(v => v.id === versionID);
                const plural = n => (n === 1 ? '' : 's');

                // NO NOTE for a version the fixed code wrote. Its empty structure
                // is a real snapshot, so promoting replaces structure exactly like
                // cues, notes and layers — which the dialog's first sentence already
                // says. Calling structure out separately made sense only while it was
                // the one thing that did NOT behave like the rest (Josh, 13 Sept:
                // "It is no different from cues, notes, layers, etc listed above").
                if (version && version.structureSnapshot === true) {
                    return '';
                }

                const pageCount = version && Array.isArray(version.pdfPageNumbers)
                    ? version.pdfPageNumbers.length
                    : null;
                const dropped = pageCount === null
                    ? 0
                    : live.filter(m => Number(m.pageIndex) > pageCount).length;

                let note = `<br><br><b>Structure:</b> this version was saved before structure was recorded in history, so it has none of its own. Your current structure (${live.length} mark${plural(live.length)}) will be kept, but it describes your current document and may not line up with this version.`;
                if (dropped > 0) {
                    note += ` ${dropped} mark${plural(dropped)} beyond page ${pageCount} will be removed.`;
                }
                return note;
            } catch (e) {
                // Never block the promote on this. Worst case the user sees the
                // original wording, which is what they saw before this existed.
                console.error('Could not check version structure:', e);
                return '';
            }
        },
        async promoteVersionConfirm(type) {
            if (this.versionToPromote === null) return;
            if (type !== 'ok') return;

            // Promotion had NO loading state at all, which was invisible while it
            // was a quick stream copy. On a large script it is not: measured on
            // staging with a 600 MB / 168-page script, the server-side PDF copy
            // alone ran 20s+ with nothing on screen, so the natural read is that
            // it failed. Users refresh, mid-promotion.
            //
            // The safety snapshot added for #386 makes that worse, not better —
            // it puts a SECOND full-PDF copy in front (29.7s on the same file) —
            // so this needs the loader whichever way round you look at it.
            this.$store.dispatch('setLoader', { type: true });
            try {
                await this.$store.dispatch('promoteVersion', this.versionToPromote);
            } catch (e) {
                console.error('Error promoting version:', e);
                this.$store.dispatch('setLoader', { type: false });
                if (this.$toast) {
                    this.$toast.error('Could not make that version live. Nothing was changed.');
                }
            }
            // On success the loader is deliberately NOT cleared here, and do not
            // "tidy" this into a
            // finally. promoteVersion ends by emitting reloadCurrentProject (or
            // routing), and that handler raises its own loader and clears it when
            // initDocument resolves. Clearing here would kill the reload's spinner
            // while the document is still loading — a worse bug than the one this
            // fixes.
        },
        deleteVersion(versionID) {
            this.versionToDelete = versionID;
            this.$refs.deleteVersionDialog.open();
        },
        deleteVersionConfirm(type) {
            if (this.versionToDelete === null) return;

            if (type === 'ok') {
                this.$store.dispatch('deleteVersion', this.versionToDelete);
            }
        },
        getFormattedDate(version) {
            return moment(version.created).format('MM/DD/YYYY hh:mm:ss a');
        },
        descendingDateComparison(a, b) {
            return moment(b, 'MM/DD/YYYY').diff(moment(a, 'MM/DD/YYYY'));
        }
    },
    created: function() {
        NavBus.$on('versions-nav.open', () => {
            if (this.$refs.versionsNav) {
                this.$refs.versionsNav.open();
            }
        });
    },
    beforeDestroy: function() {
        NavBus.$off('versions-nav.open');
    },
    components: {
        'revisions-nav': HistoryRevisionsNav,
        'version-element': VersionElement
    }
};
export default VersionsNav;

</script>

<style lang="scss">
.ql-version-list {
    .md-list-item {
        margin-top: .75em;
        line-height: 40px;
        .md-list-item-container > .md-icon:first-child  {
            margin-right: .5em !important;
        }
        .md-list-item-container > .md-button:first-child {
            margin-left: 0 !important;
        } 
    }
}
.md-sidenav-content > form {
    padding-left: .5em;
}
.ql-auto-folder {
    // The panel's md-double-line list gives every row a 72px minimum; the folder
    // header is one line, so that left a row-high blank under its title (Josh, 6 Oct).
    > .md-list-item-container {
        height: auto !important;
        min-height: 48px !important;
        align-items: center;
        padding-top: 4px;
        padding-bottom: 4px;
        // Title + "· 10 of 10" on one line in the 304px panel (Josh, 6 Oct): 15px title and
        // 14px count. Measured: "Previous live versions · 10 of 10" is ~206px of 212px.
        strong {
            font-size: 15px;
        }
        .ql-folder-count {
            font-size: 14px;
        }
    }
    .md-list-text-container {
        white-space: normal;
    }
    // Let a long title plus its "10 of 10" wrap instead of ending in an ellipsis.
    .md-list-text-container > span:first-child {
        white-space: normal;
        overflow: visible;
    }
    .ql-folder-count {
        color: rgba(0, 0, 0, .54);
        white-space: nowrap;
    }
    // Rows inside: let the label and the "before ..." line wrap rather than cut off
    // behind the three row buttons.
    .md-list-expand .md-list-item:first-child {
        margin-top: 0;
    }
    .md-list-expand .md-list-item-container {
        height: auto !important;
        min-height: 56px;
        align-items: center;
        padding-top: 6px;
        padding-bottom: 6px;
        .md-list-text-container > span {
            white-space: normal;
            line-height: 20px;
        }
    }
}
.ql-history-help {
    list-style: none;
    padding: 8px 16px 4px 52px;
    font-size: 13px;
    line-height: 18px;
    color: rgba(0, 0, 0, .54);
}
.error {
    color: red;
}
</style>


// WEBPACK FOOTER //
// versions-nav.vue?34c78b91